import { useState, useRef, SyntheticEvent } from 'react';
import csvParse from '../../helpers/csvParse';
import { Form, Col, Button, Spinner, Row } from 'react-bootstrap';
import { MatchAdmin } from '../../models/interfaces/Match';
import { RosterEntry } from '../../models/interfaces/RosterEntry';
import { MatchEntry } from '../../models/interfaces/MatchEntry';
import { PerformerTypes, TableWorker } from '../../models/interfaces/Stat';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

interface Props {
    matchAdmin: MatchAdmin;
}

export default function UploadStatCSV({ matchAdmin }: Props) {
    const [fileData, setFileData] = useState<File | null>(null);
    const fileRef = useRef(null);
    const [errorMessages, setErrorMessages] = useState<string[]>([]);

    const [isLoading, setIsLoading] = useState(false);
    const [hasCompletedUpload, setHasCompletedUpload] = useState(false);

    //add's stats one by one
    const bulkUpload = (reader: FileReader): void => {
        setIsLoading(true);
        if (typeof reader.result === 'string') {
            let statList = csvParse(reader.result);
            matchAdmin.match.lightCapTeam.rosterProvider
                .once()
                .then((lightCapPlayerResolvers) => {
                    return Promise.all(
                        lightCapPlayerResolvers.map((resolver) => {
                            return resolver.asAPromise();
                        })
                    )
                        .then((lightCapPlayers) => {
                            return matchAdmin.match.darkCapTeam.rosterProvider
                                .once()
                                .then((darkCapPlayerResolvers) => {
                                    return Promise.all(
                                        darkCapPlayerResolvers.map((resolver) => {
                                            return resolver.asAPromise();
                                        })
                                    );
                                })
                                .then((darkCapPlayers) => {
                                    return {
                                        darkCapPlayers,
                                        lightCapPlayers,
                                    };
                                });
                        })
                        .then((rosters) => {
                            return matchAdmin.start().then(() => {
                                return rosters;
                            });
                        });
                })
                .then((rosters) => {
                    const statPromises = statList.map((stat, idx) => {
                        if (stat.length < 6) return Promise.reject(`Missing items on line ${idx}`);

                        const statKey = stat[2];
                        const recordingOption = matchAdmin.recordingOptionForCSVKey(statKey);

                        if (!recordingOption) {
                            const newError = `Error on line ${idx}, could not find a stat type for key: ${statKey}`;
                            return Promise.reject(newError);
                        }

                        const teamKey = stat[0];
                        const playerKey = stat[1];
                        var performer: RosterEntry | MatchEntry | TableWorker | undefined =
                            undefined;
                        // if the recording option wants a player, try to find it.
                        if (
                            recordingOption.typeDescription.supportedPerfomers.has(
                                PerformerTypes.player
                            )
                        ) {
                            if (playerKey) {
                                const rosterSource =
                                    teamKey === 'D'
                                        ? rosters.darkCapPlayers
                                        : teamKey === 'L'
                                        ? rosters.lightCapPlayers
                                        : null;
                                if (!rosterSource) {
                                    const newError = `Error on line ${idx}, could not find a team for key: ${teamKey}`;
                                    return Promise.reject(newError);
                                }
                                performer = rosterSource.find((rosterEntry) => {
                                    return rosterEntry.capNumber.displayName === playerKey;
                                });
                            }
                        }

                        // Checking if we can handle a player or a team, verify that we don't have a player yet
                        if (
                            !performer &&
                            recordingOption.typeDescription.supportedPerfomers.has(
                                PerformerTypes.team
                            )
                        ) {
                            if (teamKey) {
                                performer =
                                    teamKey === 'D'
                                        ? matchAdmin.match.darkCapTeam
                                        : teamKey === 'L'
                                        ? matchAdmin.match.lightCapTeam
                                        : undefined;
                            }
                        }

                        if (
                            !performer &&
                            recordingOption.typeDescription.supportedPerfomers.has(
                                PerformerTypes.tableWorker
                            )
                        ) {
                            performer = {
                                user: matchAdmin.user,
                            };
                        }

                        if (!performer) {
                            const newError = `Error on line ${idx}, could not find a performer stat: ${statKey} for key: ${playerKey} for team: ${teamKey}`;
                            return Promise.reject(newError);
                        }

                        const matchSegmentKey = stat[3];
                        const matchParameters = matchAdmin.match.parameters;
                        const matchSegment =
                            matchParameters.regularTimeDescription.matchSegments.find(
                                (matchSegment) => {
                                    return matchSegment.displayName === matchSegmentKey;
                                }
                            ) ||
                            matchParameters.overtimeDescription.matchSegments.find(
                                (matchSegment) => {
                                    return matchSegment.displayName === matchSegmentKey;
                                }
                            );
                        if (!matchSegment) {
                            const newError = `Error on line ${idx}, could not find a quarter or overtime for key: ${matchSegmentKey}`;
                            return Promise.reject(newError);
                        }

                        const minutes = parseInt(stat[4]);
                        const seconds = parseInt(stat[5]);
                        if (typeof minutes !== 'number' || typeof seconds !== 'number') {
                            const newError = `Error on line ${idx}, minutes: ${minutes}, seconds: ${seconds} failed to parse into a timestamp`;
                            return Promise.reject(newError);
                        }
                        const note = stat[6];

                        const timestamp = minutes * 60 + seconds;
                        return matchAdmin
                            .addStat(
                                matchSegment,
                                timestamp,
                                performer,
                                recordingOption.typeDescription,
                                note
                            )
                            .catch((error) => {
                                const newError = `Error on line ${idx}, failed to save stat metadata: ${error}`;
                                return Promise.reject(newError);
                            });
                    });

                    Promise.allSettled(statPromises)
                        .then((resolvedStatPromises) => {
                            const newErrorMessages = resolvedStatPromises.reduce(
                                (currentErrorMessages, resolvedStatPromise) => {
                                    if (resolvedStatPromise.status === 'rejected') {
                                        const errorMessage = resolvedStatPromise.reason;
                                        if (typeof errorMessage === 'string') {
                                            return currentErrorMessages.concat([errorMessage]);
                                        } else {
                                            return currentErrorMessages.concat([
                                                'Failed to extract error message when uploading a stat',
                                            ]);
                                        }
                                    } else {
                                        return currentErrorMessages;
                                    }
                                },
                                new Array<string>()
                            );
                            setErrorMessages(newErrorMessages);
                        })
                        .finally(() => {
                            setHasCompletedUpload(true);
                            setIsLoading(false);
                        });
                });
        }
    };

    const handleSubmit = (e: SyntheticEvent) => {
        e.preventDefault();
        let reader = new FileReader();
        if (fileData) {
            //run's once fileReader has read text which is an async function. onload needs to be declared before readAsText is initialized
            reader.onload = () => bulkUpload(reader);
            reader.readAsText(fileData);
        }
    };
    return (
        <Col>
            <Row className="text-center">
                <Col>
                    <strong>Stat CSV Upload</strong>
                </Col>
            </Row>
            {isLoading ? (
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
            ) : (
                !hasCompletedUpload && (
                    <Form onSubmit={handleSubmit} inline>
                        <Col xs={12} md={!fileData ? 12 : 8}>
                            <Form.File id="stat-upload" custom>
                                <Form.File.Input
                                    onChange={(e: any) => {
                                        setFileData(e.target.files[0]);
                                    }}
                                    accept=".csv"
                                />
                                <Form.File.Label
                                    data-browse="Select"
                                    className="justify-content-start"
                                >
                                    {fileData ? fileData.name : 'Select file to upload'}
                                </Form.File.Label>
                            </Form.File>
                        </Col>
                        {!!fileData && (
                            <Col xs={12} md={4}>
                                <Button
                                    variant="success"
                                    className="btn-block"
                                    type="submit"
                                    ref={fileRef}
                                >
                                    Upload
                                </Button>
                            </Col>
                        )}
                    </Form>
                )
            )}
            {hasCompletedUpload &&
                (errorMessages.length > 0 ? (
                    <>
                        Upload completed with errors, please see the csv lines referenced below for
                        more information.
                    </>
                ) : (
                    <>Upload completed! Please finish the match and review the timeline.</>
                ))}
            {errorMessages.length > 0 && (
                <Col>
                    <Row className="mt-2">
                        <Col>Upload Errors</Col>
                        {errorMessages.length > 1 && (
                            <Button
                                onClick={() => {
                                    setErrorMessages([]);
                                }}
                            >
                                Clear All
                            </Button>
                        )}
                    </Row>
                    {errorMessages.map((message, idx) => {
                        return (
                            <Row key={`errorMessage${idx}: ${message}`}>
                                <Col>
                                    <Button
                                        size="sm"
                                        variant="danger"
                                        onClick={() => {
                                            setErrorMessages((errorMessages) => {
                                                return errorMessages.filter((errorMessage) => {
                                                    return errorMessage !== message;
                                                });
                                            });
                                        }}
                                    >
                                        {<FontAwesomeIcon icon={faTimes as IconProp} />}
                                    </Button>
                                </Col>
                                <div className="text-danger">{message}</div>
                            </Row>
                        );
                    })}
                </Col>
            )}
        </Col>
    );
}
